/**
 * Authentication API client
 * Handles all auth-related endpoints
 */

import type { ApiResponse } from '../types';
import { ConfigStore } from '../state/config-store';
import { AuthStore } from '../state/auth-store';

export class AuthAPI {
  private configStore: ConfigStore;
  private authStore: AuthStore;

  constructor(configStore: ConfigStore, authStore: AuthStore) {
    this.configStore = configStore;
    this.authStore = authStore;
  }

  async signup(email: string, password: string, turnstileToken?: string): Promise<ApiResponse> {
    const body: any = { email, password };
    if (turnstileToken) {
      body['cf-turnstile-response'] = turnstileToken;
    }
    return this.apiCall('/v1/auth/signup', 'POST', body);
  }

  async login(email: string, password: string): Promise<ApiResponse> {
    return this.apiCall('/v1/auth/login', 'POST', { email, password });
  }

  async verifyEmail(email: string, code: string): Promise<ApiResponse> {
    return this.apiCall('/v1/auth/verify-email', 'POST', { email, code });
  }

  async forgotPassword(email: string, turnstileToken?: string): Promise<ApiResponse> {
    const body: any = { email };
    if (turnstileToken) {
      body['cf-turnstile-response'] = turnstileToken;
    }
    return this.apiCall('/v1/auth/forgot-password', 'POST', body);
  }

  async resetPassword(
    email: string,
    code: string,
    newPassword: string,
    turnstileToken?: string
  ): Promise<ApiResponse> {
    const body: any = { email, code, newPassword };
    if (turnstileToken) {
      body['cf-turnstile-response'] = turnstileToken;
    }
    return this.apiCall('/v1/auth/reset-password', 'POST', body);
  }

  async resendCode(email: string): Promise<ApiResponse> {
    return this.apiCall('/v1/auth/resend-code', 'POST', { email });
  }

  async refreshToken(refreshToken: string): Promise<ApiResponse> {
    return this.apiCall('/v1/auth/refresh', 'POST', { refreshToken });
  }

  async validateToken(token: string): Promise<ApiResponse> {
    return this.apiCall('/v1/auth/validate', 'POST', { token }, true);
  }

  async signout(globalSignOut: boolean): Promise<ApiResponse> {
    const body: any = {
      token: this.authStore.getIdToken(),
      accessToken: this.authStore.getAccessToken(),
      globalSignOut,
    };
    return this.apiCall('/v1/auth/signout', 'POST', body, true);
  }

  async invalidateSessions(): Promise<ApiResponse> {
    const body: any = {
      accessToken: this.authStore.getAccessToken(),
    };
    return this.apiCall('/v1/auth/invalidate-sessions', 'POST', body, true);
  }

  async createCheckoutSession(
    successUrl: string,
    cancelUrl: string,
    billingCycle: string
  ): Promise<ApiResponse> {
    return this.apiCall(
      '/v1/state/checkout/session',
      'POST',
      { successUrl, cancelUrl, billingCycle },
      true
    );
  }

  async cancelSubscription(subscriptionId: string, atPeriodEnd: boolean): Promise<ApiResponse> {
    return this.apiCall(
      '/v1/state/subscription/cancel',
      'POST',
      { subscriptionId, atPeriodEnd },
      true
    );
  }

  private async apiCall(
    endpoint: string,
    method: string,
    body?: any,
    requiresAuth: boolean = false
  ): Promise<ApiResponse> {
    const baseUrl = this.configStore.getConfig().apiBaseUrl;
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (requiresAuth) {
      const token = this.authStore.getIdToken();
      if (!token) {
        throw new Error('No auth token available. Please login first.');
      }
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${baseUrl}${endpoint}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    const data = await response.json().catch(() => ({}));
    return { status: response.status, data, headers: response.headers };
  }
}
